import React, { useEffect, useState } from "react";
import { Users } from "lucide-react";
import { toast } from "sonner";
import { client } from "@/lib/api";
import { useAuth } from "@/components/AuthProvider";
import { isInsider } from "@/lib/levels";

/**
 * Wave C point P (Vania 7 Oct 2026) · "send to squad".
 *
 * Supersedes the broken "add to squad" button on event pages. On tap:
 *   · guest → the get in sheet (never a dead end)
 *   · in    → the become an insider sheet (never a dead end)
 *   · insider → bottom drawer listing their squads (avatar, name, member count).
 *     Pick one or more, tap "send", and the event lands in each squad's
 *     thread as an `event_share` yap message (the real event card renders
 *     on tap because the thread already has an event_share renderer).
 *     Toasts "sent to <squad>" (or "sent to 2 squads" for multi-pick).
 *   · insider with no squads → inline "start a squad" field.
 *
 * Why the old button errored: it required an invitee @handle input when no
 * `targetUserId` was passed (event pages pass none), so pressing it with no
 * handle hit `toast.error("who are we inviting?")`. Even when a handle was
 * typed, it called `/squads/{id}/invite` to add that person to a squad —
 * which is NOT what "add to squad" should mean on an event page. The event
 * was never sent. The real action is a message, not a member invite.
 */
const YELLOW = "#FFD60A";
const INK = "#1A1A30";

export default function SendToSquadButton({ eventId, testIdPrefix = "", compact = false }) {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [squads, setSquads] = useState(null);
  const [picked, setPicked] = useState(new Set());
  const [busy, setBusy] = useState(false);
  const [newSquadName, setNewSquadName] = useState("");

  useEffect(() => {
    if (!open || !user || !isInsider(user)) return;
    setSquads(null); setPicked(new Set());
    client.get("/squads/mine").then((r) => setSquads(r.data.squads || []))
      .catch(() => setSquads([]));
  }, [open, user]);

  const openDrawer = (e) => {
    e?.stopPropagation?.();
    if (!user) {
      // guest · open the get in sheet
      try { window.dispatchEvent(new Event("ww:getin:open")); } catch {}
      return;
    }
    if (!isInsider(user)) {
      // free "in" member · open the become an insider sheet
      try { window.dispatchEvent(new Event("ww:unlocks:open")); } catch {}
      return;
    }
    setOpen(true);
  };

  const togglePick = (sqId) => {
    setPicked((cur) => {
      const next = new Set(cur);
      if (next.has(sqId)) next.delete(sqId);
      else next.add(sqId);
      return next;
    });
  };

  const sendToPicked = async () => {
    if (!eventId || busy) return;
    if (picked.size === 0) { toast.error("pick at least one squad"); return; }
    setBusy(true);
    try {
      const ids = Array.from(picked);
      // Fire the event_share message into each selected squad thread.
      // Thread id for a squad is `squad:{squad_id}` per yap backend.
      await Promise.all(ids.map((sqId) =>
        client.post(`/yap/threads/squad:${sqId}/messages`, {
          kind: "event_share", shared_event_id: eventId,
        })
      ));
      const names = ids.map((sqId) => (squads || []).find((s) => s.id === sqId)?.name || "squad");
      if (ids.length === 1) toast.success(`sent to ${names[0]}`.toLowerCase());
      else toast.success(`sent to ${ids.length} squads`);
      setOpen(false);
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "string" ? d.replace(/_/g, " ") : "could not send");
    } finally { setBusy(false); }
  };

  const createAndSend = async () => {
    const name = (newSquadName || "").trim();
    if (!name) { toast.error("name the squad first"); return; }
    setBusy(true);
    try {
      const r = await client.post("/squads", { name });
      const sq = r.data.squad;
      await client.post(`/yap/threads/squad:${sq.id}/messages`, {
        kind: "event_share", shared_event_id: eventId,
      });
      toast.success(`sent to ${name}`.toLowerCase());
      setOpen(false);
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "string" ? d.replace(/_/g, " ") : "could not create squad");
    } finally { setBusy(false); }
  };

  const Btn = (
    <button data-testid={`${testIdPrefix}send-to-squad-btn`}
            onClick={openDrawer}
            aria-label="send to squad"
            className={compact ? "" : "inline-flex items-center gap-1 rounded-full px-3 py-2 text-xs lowercase"}
            style={compact ? {
              width: 44, height: 44, borderRadius: 999,
              background: "var(--ww-surface)", border: "1px solid #2F2F52",
              color: "var(--ww-text)", display: "grid", placeItems: "center",
              cursor: "pointer",
            } : {
              background: "transparent", border: `1px solid ${YELLOW}`,
              color: "var(--ww-text)", fontFamily: "Poppins", fontWeight: 600,
              cursor: "pointer",
            }}>
      <Users size={compact ? 18 : 13} strokeWidth={2} />
      {!compact && <span>send to squad</span>}
    </button>
  );

  return (
    <>
      {Btn}
      {open && (
        <div data-testid={`${testIdPrefix}send-to-squad-drawer`}
             onMouseDown={(e) => { if (e.target === e.currentTarget) setOpen(false); }}
             style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.55)", zIndex: 70,
                      display: "flex", flexDirection: "column", justifyContent: "flex-end" }}>
          <div style={{ background: "var(--ww-ground)", color: "var(--ww-text)",
                        borderTopLeftRadius: 20, borderTopRightRadius: 20,
                        padding: "20px 20px 24px", maxWidth: 520, width: "100%",
                        margin: "0 auto",
                        borderTop: "1px solid var(--ww-line)" }}>
            <div style={{ width: 36, height: 4, borderRadius: 999, background: "var(--ww-line-strong)",
                           margin: "0 auto 14px" }} />
            <p style={{ fontFamily: "Poppins", fontWeight: 800, fontSize: 18, marginBottom: 4,
                        textTransform: "lowercase" }}>
              send to squad
            </p>
            <p style={{ fontFamily: "Arial", fontSize: 13, color: "var(--ww-text-muted)",
                        marginBottom: 14 }}>
              pick one or more. the event lands in the squad's chat.
            </p>
            {squads === null ? (
              <p style={{ fontFamily: "Arial", fontSize: 13, color: "var(--ww-text-muted)" }}>loading your squads…</p>
            ) : squads.length === 0 ? (
              <div data-testid={`${testIdPrefix}send-to-squad-empty`}>
                <p style={{ fontFamily: "Arial", fontSize: 13, color: "var(--ww-text-muted)", marginBottom: 10 }}>
                  no squads yet. name one to start:
                </p>
                <input value={newSquadName}
                       onChange={(e) => setNewSquadName(e.target.value)}
                       placeholder="my crew"
                       data-testid={`${testIdPrefix}send-to-squad-newname`}
                       style={{ width: "100%", padding: "12px 14px",
                                borderRadius: 12, background: "var(--ww-surface)",
                                border: "1px solid var(--ww-line)", color: "var(--ww-text)",
                                fontFamily: "Arial", fontSize: 14, marginBottom: 12 }} />
                <button disabled={busy}
                        data-testid={`${testIdPrefix}send-to-squad-create`}
                        onClick={createAndSend}
                        style={{ width: "100%", padding: "14px 16px", borderRadius: 999,
                                 background: YELLOW, color: INK, border: 0,
                                 fontFamily: "Poppins", fontWeight: 800, fontSize: 15,
                                 cursor: busy ? "not-allowed" : "pointer", minHeight: 48,
                                 textTransform: "lowercase" }}>
                  start a squad and send
                </button>
              </div>
            ) : (
              <>
                <ul data-testid={`${testIdPrefix}send-to-squad-list`}
                    style={{ display: "flex", flexDirection: "column", gap: 8,
                             maxHeight: 320, overflowY: "auto", margin: 0, padding: 0, listStyle: "none" }}>
                  {squads.map((s) => {
                    const on = picked.has(s.id);
                    return (
                      <li key={s.id}>
                        <button disabled={busy}
                                data-testid={`${testIdPrefix}send-to-squad-pick-${s.id}`}
                                onClick={() => togglePick(s.id)}
                                aria-pressed={on}
                                style={{ width: "100%", display: "flex", alignItems: "center",
                                         gap: 12, padding: "12px 14px",
                                         borderRadius: 12, background: on ? "var(--ww-surface)" : "transparent",
                                         border: `1px solid ${on ? YELLOW : "var(--ww-line)"}`,
                                         color: "var(--ww-text)", textAlign: "left",
                                         fontFamily: "Poppins", fontSize: 14, cursor: "pointer" }}>
                          <span style={{ width: 36, height: 36, borderRadius: 999,
                                          background: "var(--ww-sunken)",
                                          display: "grid", placeItems: "center",
                                          color: "var(--ww-text)",
                                          fontFamily: "Poppins", fontWeight: 800, fontSize: 14 }}>
                            {(s.name || "?").slice(0, 1).toLowerCase()}
                          </span>
                          <span style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: 2 }}>
                            <span style={{ fontWeight: 700, textTransform: "lowercase",
                                            overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                              {s.name}
                            </span>
                            <span style={{ fontFamily: "Arial", fontSize: 12, color: "var(--ww-text-muted)" }}>
                              {s.member_count || s.members_count || (s.members || []).length || 1} members
                            </span>
                          </span>
                          <span style={{ width: 20, height: 20, borderRadius: 999,
                                          background: on ? YELLOW : "transparent",
                                          border: `2px solid ${on ? YELLOW : "var(--ww-line-strong)"}`,
                                          display: "grid", placeItems: "center" }}>
                            {on && <span style={{ width: 8, height: 8, borderRadius: 999, background: INK }} />}
                          </span>
                        </button>
                      </li>
                    );
                  })}
                </ul>
                <button disabled={busy || picked.size === 0}
                        data-testid={`${testIdPrefix}send-to-squad-send`}
                        onClick={sendToPicked}
                        style={{ marginTop: 14, width: "100%", padding: "14px 16px",
                                 borderRadius: 999,
                                 background: picked.size ? YELLOW : "var(--ww-sunken)",
                                 color: picked.size ? INK : "var(--ww-text-muted)",
                                 border: 0, fontFamily: "Poppins", fontWeight: 800, fontSize: 15,
                                 cursor: (busy || !picked.size) ? "not-allowed" : "pointer",
                                 minHeight: 48, textTransform: "lowercase" }}>
                  send
                </button>
              </>
            )}
            <button onClick={() => setOpen(false)}
                    data-testid={`${testIdPrefix}send-to-squad-cancel`}
                    style={{ marginTop: 10, width: "100%", padding: "10px",
                             background: "transparent", color: "var(--ww-text-muted)",
                             border: 0, cursor: "pointer", fontFamily: "Arial", fontSize: 13,
                             textTransform: "lowercase" }}>
              cancel
            </button>
          </div>
        </div>
      )}
    </>
  );
}
